/* eslint-disable no-debugger */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import {
  getClipBroadcastId,
  getClipFPS,
  isLayoutWithVisionDataEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { timeStringToSeconds } from '@goldcast/player-config/src/utils/timeUtils';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { LayoutChange } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/LayoutChange';
import { VideoCropPosition } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Scene';
import { getCropPosition } from '@goldcast/player-config/src/utils/cropUtils';
import { getFinalConfigForLayout } from '@goldcast/player-config/src/utils/layout';
import { getAspectRatio } from '@goldcast/player-config/src/utils/sizeUtils';
import { CSSProperties } from '@goldcast/player-config/src/types/css-properties';
import { selectSpeakerAnalysis } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import { CropSelectionOutline } from './CropSelectionOutline';
import { getDebugVideoCropBoundsForUploads } from './videoCropBoundsUtils';
import ContentDialog from '@/Pages/Sessions/uiComponents/ContentDialog/ContentDialog';
import Button from '@/components/atoms/Button/Button';
import { updateClipData } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtils/constants';
import { currentClip } from '@/stores/clip';
import { remotionPlayerStateStore } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/store/remotionPlayerState';
import { RemotionPlayerContext } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/context/RemotionPlayerContext';
import { formatTime } from '@/libs/core';
import Icon from '@/components/atoms/Icon';
import { useTimelineSpeakers } from '@/hooks/useTimelineSpeakers';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { getCompositeSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';
import useAnalytics from '@/hooks/useAnalytics';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import Toggle from '@/components/atoms/Toggle';

interface DebugVideoCropContainerProps {
  videoUrl: string;
  clipId: string;
  selectedVideoId: string;
  compositeSegmentId: string;
  isOpen: boolean;
  onClose: () => void;
  onCropComplete?: (cropData: CropData) => void;
  containerAspectRatio: number;
  videoAspectRatio: number;
  maintainAspectRatio?: boolean;
}

interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DebugCropBounds {
  facialBound?: Record<string, VideoCropPosition>;
  personBound?: Record<string, VideoCropPosition>;
  personContainerBound?: Record<string, VideoCropPosition>;
  videoCropPositionMap: Record<string, VideoCropPosition>;
  faceBoundingBoxMap: Record<string, VideoCropPosition>;
  personContainerBoundingBoxMap: Record<string, VideoCropPosition>;
  availableSpeakerIds: string[];
}

const getLayoutChangeCropPositions = (
  layoutChange: LayoutChange,
  selectedVideoId: string,
  parentContainerAspectRatio: number,
  videoAspectRatio: number
) => {
  const oldCropPositions = layoutChange.videoCropPositionMap?.[selectedVideoId];
  if (!oldCropPositions) {
    const shouldFitByWidth = parentContainerAspectRatio > videoAspectRatio;
    if (shouldFitByWidth) {
      return {
        left: 0,
        top: 0.5 - (0.5 * videoAspectRatio) / parentContainerAspectRatio,
        width: 1,
        height: (1 * videoAspectRatio) / parentContainerAspectRatio
      };
    }
    return {
      top: 0,
      left: 0.5 - (0.5 * parentContainerAspectRatio) / videoAspectRatio,
      width: (1 * parentContainerAspectRatio) / videoAspectRatio,
      height: 1
    };
  }
  return {
    left: oldCropPositions.top_left.x || 0,
    top: oldCropPositions.top_left.y || 0,
    width: oldCropPositions.bottom_right.x - oldCropPositions.top_left.x || 1,
    height: oldCropPositions.bottom_right.y - oldCropPositions.top_left.y || 1
  };
};

export default function DebugVideoCropContainer({
  videoUrl,
  clipId,
  selectedVideoId,
  compositeSegmentId,
  isOpen,
  onClose,
  maintainAspectRatio = true,
  containerAspectRatio,
  videoAspectRatio
}: DebugVideoCropContainerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { playerRef } = useContext(RemotionPlayerContext);

  const [videoLoading, setVideoLoading] = useState(true);

  // Get speakers analysis data
  const clip = currentClip.getSnapshot()[clipId];
  const fps = clip ? getClipFPS(clip) : 30;
  const compositeSegment = clip && getCompositeSegment(clip, compositeSegmentId);
  const trimOffset = roundToNDecimalPlaces(compositeSegment ? compositeSegment.currentBounds.fromFrame / fps : 0, 4);
  const offset = roundToNDecimalPlaces(compositeSegment ? compositeSegment.fromFrame / fps : 0, 4);
  const [selectedSpeakerId, setSelectedSpeakerId] = useState<string>(selectedVideoId);
  const { speakers } = useTimelineSpeakers();
  const { seekTo } = useRemotionPlayerSeek();

  const [currentCompositeFrame, setCurrentCompositeFrame] = useState(
    roundToNDecimalPlaces((playerRef?.getCurrentFrame() || 0) + trimOffset * fps - offset * fps, 4)
  );

  const speakersAnalysis = useContentMetadataStore(selectSpeakerAnalysis(getClipBroadcastId(clip) ?? ''));

  // Get speaker details and video-to-speaker mapping

  const [selectedTimeAnalysisIndex, setSelectedTimeAnalysisIndex] = useState<number | null>(null);
  const [showBoundForSelectedSpeakerOnly, setShowBoundForSelectedSpeakerOnly] = useState<boolean>(false);
  const [usePersonContainerForPreview, setUsePersonContainerForPreview] = useState(true);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isPersonContainerFeatureEnabled = Boolean(
    featureFlags[FeatureFlagKeys.Use_CL_Person_Container] &&
      clip &&
      isLayoutWithVisionDataEnabledPure({
        clip,
        isFullFaceRecEnabled: Boolean(featureFlags[FeatureFlagKeys.Use_CL_Fullvideo_Facerec]),
        isFullFaceRecForPreRecordedEnabled: Boolean(featureFlags[FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded])
      })
  );
  // Get the speaker key for the selected video

  const currentLayoutChange = useMemo(() => {
    if (!compositeSegment) {
      return null;
    }
    return compositeSegment.layoutChanges.find(
      layoutChange =>
        roundToNDecimalPlaces(layoutChange.fromFrame, 4) <=
          roundToNDecimalPlaces(currentCompositeFrame - trimOffset * fps, 4) &&
        roundToNDecimalPlaces(layoutChange.toFrame, 4) >
          roundToNDecimalPlaces(currentCompositeFrame - trimOffset * fps, 4)
    );
  }, [compositeSegment, currentCompositeFrame, trimOffset, fps]);

  const speakersInTheLayout = useMemo(() => {
    return speakers.filter(speaker => currentLayoutChange?.videoIds.includes(speaker.key));
  }, [currentLayoutChange?.videoIds, speakers]);

  const [cropPositions, setCropPositions] = useState(
    currentLayoutChange && selectedSpeakerId
      ? getLayoutChangeCropPositions(currentLayoutChange, selectedSpeakerId, containerAspectRatio, videoAspectRatio)
      : {
          left: 0,
          top: 0,
          width: 1,
          height: 1
        }
  );

  useEffect(() => {
    if (currentLayoutChange && selectedSpeakerId) {
      setCropPositions(
        getLayoutChangeCropPositions(currentLayoutChange, selectedSpeakerId, containerAspectRatio, videoAspectRatio)
      );
    }
  }, [containerAspectRatio, currentLayoutChange, selectedSpeakerId, videoAspectRatio]);

  useEffect(() => {
    if (videoRef.current && !videoLoading && isOpen) {
      videoRef.current.currentTime = currentCompositeFrame / fps;
    }
  }, [currentCompositeFrame, fps, videoLoading, isOpen]);

  // Calculate initial time analysis index based on current time
  const initialTimeIndex = useMemo(() => {
    if (!speakersAnalysis || !speakersAnalysis.time_analysis || speakersAnalysis.time_analysis.length === 0) {
      return 0;
    }
    const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
    if (!compositeSegment) {
      return 0;
    }
    return Math.max(
      speakersAnalysis.times.findLastIndex(t => t <= currentTime),
      0
    );
  }, [compositeSegment, speakersAnalysis]);

  // Initialize selected time analysis index when dialog opens
  useEffect(() => {
    if (isOpen && selectedTimeAnalysisIndex === null) {
      setSelectedTimeAnalysisIndex(initialTimeIndex);
    }
    // Reset when dialog closes
    if (!isOpen) {
      setSelectedTimeAnalysisIndex(null);
      setSelectedSpeakerId('');
      setShowBoundForSelectedSpeakerOnly(false);
      setUsePersonContainerForPreview(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialTimeIndex]);

  // Handle time analysis selection
  const handleTimeAnalysisSelect = useCallback(
    (index: number) => {
      if (!speakersAnalysis || !speakersAnalysis.time_analysis || !clip) {
        return;
      }

      setSelectedTimeAnalysisIndex(index);

      // Use the precomputed absolute time from speakersAnalysis.times
      const absoluteTime = speakersAnalysis.times[index];
      if (absoluteTime === undefined) {
        return;
      }

      // Calculate absolute time considering clip start and composite segment offsets
      if (!compositeSegment) {
        return;
      }

      setCurrentCompositeFrame(roundToNDecimalPlaces(Math.max(0, absoluteTime * fps), 4));
    },
    [speakersAnalysis, clip, compositeSegment, fps]
  );

  const setIsOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose]
  );

  const updateCropPositions = useCallback(newPositions => {
    setCropPositions(newPositions);
  }, []);
  const { trackManualRepositioningApplied } = useAnalytics();

  const handleCropComplete = useCallback(() => {
    const newCropPositions = {
      top_left: {
        x: cropPositions.left,
        y: cropPositions.top
      },
      bottom_right: {
        x: cropPositions.left + cropPositions.width,
        y: cropPositions.top + cropPositions.height
      }
    };
    if (!compositeSegment) {
      return;
    }
    const currentScene = compositeSegment.scenes.find(
      scene => scene.fromFrame <= currentCompositeFrame && scene.toFrame > currentCompositeFrame
    );
    if (!currentScene) {
      return;
    }
    updateClipData({
      clipId,
      actionType: CLIP_UPDATE_TYPE.UPDATE_SCENE_CROP_POSITION,
      compositeId: compositeSegmentId,
      cropPosition: newCropPositions,
      sceneId: currentScene.id,
      videoId: selectedSpeakerId
    });
    trackManualRepositioningApplied({ clipId, fromRemotionPlayer: true });
    seekTo(currentCompositeFrame / fps - trimOffset + offset);
    onClose();
  }, [
    clipId,
    compositeSegment,
    compositeSegmentId,
    cropPositions.height,
    cropPositions.left,
    cropPositions.top,
    cropPositions.width,
    currentCompositeFrame,
    fps,
    offset,
    onClose,
    seekTo,
    selectedSpeakerId,
    trackManualRepositioningApplied,
    trimOffset
  ]);

  const {
    facialBound,
    personBound,
    personContainerBound,
    videoCropPositionMap,
    faceBoundingBoxMap,
    personContainerBoundingBoxMap,
    availableSpeakerIds
  } = useMemo<DebugCropBounds>(() => {
    const emptyBounds: DebugCropBounds = {
      videoCropPositionMap: {},
      faceBoundingBoxMap: {},
      personContainerBoundingBoxMap: {},
      availableSpeakerIds: []
    };
    if (!clip || !compositeSegment || !currentLayoutChange || !speakersAnalysis?.time_analysis?.length) {
      return emptyBounds;
    }

    // Use selected time analysis index if available, otherwise fall back to current time
    let timeIndex: number;
    if (selectedTimeAnalysisIndex !== null && selectedTimeAnalysisIndex >= 0) {
      timeIndex = selectedTimeAnalysisIndex;
    } else {
      const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
      timeIndex = Math.max(
        speakersAnalysis.times.findLastIndex(t => t <= currentTime),
        0
      );
    }

    // Ensure index is within bounds
    timeIndex = Math.max(0, Math.min(timeIndex, speakersAnalysis.time_analysis.length - 1));

    const timeAnalysisPoint = speakersAnalysis.time_analysis[timeIndex];
    const facePositions = timeAnalysisPoint.face_positions;

    const bounds = getDebugVideoCropBoundsForUploads({
      facePositions: structuredClone(facePositions),
      speakerIdToFaceMap: speakersAnalysis.speaker_mapping,
      broadcastId: getClipBroadcastId(clip)!
    });
    if (!bounds) {
      return emptyBounds;
    }

    const {
      videoCropPositionMap,
      faceBoundingBoxMap,
      personBoundingBoxMap,
      personContainerBoundingBoxMap = {}
    } = bounds;

    // Get available speaker IDs - only speakers in layout who have person bounds
    const availableIds =
      speakersInTheLayout?.filter(speaker => personBoundingBoxMap?.[speaker.key]).map(speaker => speaker.key) || [];

    // Filter person bounds to show only selected speaker if checkbox is checked
    let filteredPersonBoundingBoxMap = personBoundingBoxMap;
    let filteredFaceBoundingBoxMap = faceBoundingBoxMap;
    let filteredPersonContainerBoundingBoxMap = personContainerBoundingBoxMap;
    if (showBoundForSelectedSpeakerOnly && selectedSpeakerId && personBoundingBoxMap) {
      // selectedSpeakerId is already a videoId, so use it directly
      if (personBoundingBoxMap[selectedSpeakerId]) {
        filteredPersonBoundingBoxMap = {
          [selectedSpeakerId]: personBoundingBoxMap[selectedSpeakerId]
        };
        filteredFaceBoundingBoxMap = faceBoundingBoxMap?.[selectedSpeakerId]
          ? {
              [selectedSpeakerId]: faceBoundingBoxMap?.[selectedSpeakerId]
            }
          : filteredFaceBoundingBoxMap;
        filteredPersonContainerBoundingBoxMap = personContainerBoundingBoxMap[selectedSpeakerId]
          ? {
              [selectedSpeakerId]: personContainerBoundingBoxMap[selectedSpeakerId]
            }
          : {};
      } else {
        filteredPersonBoundingBoxMap = {};
        filteredFaceBoundingBoxMap = {};
        filteredPersonContainerBoundingBoxMap = {};
      }
    }

    return {
      facialBound: filteredFaceBoundingBoxMap,
      personBound: filteredPersonBoundingBoxMap,
      personContainerBound: filteredPersonContainerBoundingBoxMap,
      videoCropPositionMap,
      faceBoundingBoxMap,
      personContainerBoundingBoxMap,
      availableSpeakerIds: availableIds
    };
  }, [
    clip,
    compositeSegment,
    currentLayoutChange,
    selectedTimeAnalysisIndex,
    speakersInTheLayout,
    showBoundForSelectedSpeakerOnly,
    selectedSpeakerId,
    speakersAnalysis
  ]);

  const calculatedCropPosition = useMemo(() => {
    if (!isPersonContainerFeatureEnabled || !currentLayoutChange || !selectedSpeakerId || !clip) {
      return undefined;
    }
    const videoIndex = currentLayoutChange.videoIds.indexOf(selectedSpeakerId);
    const finalConfig = getFinalConfigForLayout(currentLayoutChange, currentLayoutChange.videoIds);
    const selectedCropPosition = videoCropPositionMap[selectedSpeakerId];
    const selectedConfig = finalConfig[videoIndex];
    if (videoIndex < 0 || !selectedCropPosition || !selectedConfig?.containerStyle) {
      return undefined;
    }

    const allOtherCropInformation = {
      ...Object.fromEntries(Object.entries(faceBoundingBoxMap).filter(([videoId]) => videoId !== selectedSpeakerId)),
      ...Object.fromEntries(
        Object.entries(videoCropPositionMap).filter(
          ([videoId]) => videoId !== selectedSpeakerId && currentLayoutChange.videoIds.includes(videoId)
        )
      )
    };

    return getCropPosition(
      selectedCropPosition,
      selectedConfig.containerStyle as CSSProperties,
      getAspectRatio(clip.asset_metadata.size),
      allOtherCropInformation,
      false,
      videoAspectRatio,
      faceBoundingBoxMap[selectedSpeakerId],
      usePersonContainerForPreview ? personContainerBoundingBoxMap[selectedSpeakerId] : undefined
    );
  }, [
    clip,
    currentLayoutChange,
    faceBoundingBoxMap,
    isPersonContainerFeatureEnabled,
    personContainerBoundingBoxMap,
    selectedSpeakerId,
    usePersonContainerForPreview,
    videoAspectRatio,
    videoCropPositionMap
  ]);

  useEffect(() => {
    if (!calculatedCropPosition) {
      return;
    }
    setCropPositions({
      left: calculatedCropPosition.top_left.x,
      top: calculatedCropPosition.top_left.y,
      width: calculatedCropPosition.bottom_right.x - calculatedCropPosition.top_left.x,
      height: calculatedCropPosition.bottom_right.y - calculatedCropPosition.top_left.y
    });
  }, [calculatedCropPosition]);

  useEffect(() => {
    if (isOpen) {
      setSelectedSpeakerId(selectedVideoId);
    }
  }, [isOpen, selectedVideoId]);

  // Validate and update selected speaker when time analysis changes
  useEffect(() => {
    if (!isOpen || availableSpeakerIds.length === 0) {
      return;
    }

    // If selected speaker is not available in current time analysis, switch to first available
    if (selectedSpeakerId && !availableSpeakerIds.includes(selectedSpeakerId)) {
      const firstAvailableSpeakerId = availableSpeakerIds[0];
      if (firstAvailableSpeakerId) {
        setSelectedSpeakerId(firstAvailableSpeakerId);
      }
    } else if (!selectedSpeakerId && availableSpeakerIds.length > 0) {
      // If no speaker is selected, select the first available one
      setSelectedSpeakerId(availableSpeakerIds[0]);
    }
  }, [isOpen, selectedSpeakerId, availableSpeakerIds]);

  return (
    <div onPointerDown={e => e.stopPropagation()}>
      <ContentDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={'Crop Video'}
        size="large"
        panelClassNames="!max-w-6xl"
      >
        <div className="flex flex-col p-6">
          <div className="mb-6 flex gap-4">
            {/* Speakers Panel */}
            <div className="flex w-48 flex-col border-r border-slate-200 pr-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Speakers</h3>
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showPersonBoundForSelectedOnly"
                  checked={showBoundForSelectedSpeakerOnly}
                  onChange={e => setShowBoundForSelectedSpeakerOnly(e.target.checked)}
                  className="size-4 rounded border-gray-300 text-deep-orange focus:ring-2 focus:ring-theme-input-focus focus:ring-offset-2"
                />
                <label htmlFor="showPersonBoundForSelectedOnly" className="text-xs text-slate-600">
                  Show bound for selected only
                </label>
              </div>
              <div className="flex max-h-[calc(75vh-200px)] flex-col gap-2 overflow-y-auto">
                {speakers.length > 0 ? (
                  speakers.map((speakerInfo, index) => {
                    const isSelected = selectedSpeakerId === speakerInfo.key;
                    const isAvailable = speakerInfo.key ? availableSpeakerIds.includes(speakerInfo.key) : false;

                    return (
                      <button
                        key={speakerInfo.key}
                        onClick={() => {
                          if (isAvailable) {
                            setSelectedSpeakerId(speakerInfo.key || '');
                          }
                        }}
                        disabled={!isAvailable}
                        className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : !isAvailable
                              ? 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                        title={
                          isSelected
                            ? 'Current video speaker'
                            : !isAvailable
                              ? 'Speaker not available in this time analysis'
                              : ''
                        }
                      >
                        {speakerInfo?.first_name || speakerInfo?.last_name
                          ? `${speakerInfo.first_name || ''} ${speakerInfo.last_name || ''}`.trim()
                          : speakerInfo?.key || `Speaker ${index + 1}`}
                        {isSelected && <span className="ml-1 text-xs">(Current)</span>}
                        {!isAvailable && !isSelected && <span className="ml-1 text-xs">(Unavailable)</span>}
                      </button>
                    );
                  })
                ) : (
                  <div className="py-4 text-center text-sm text-slate-500">No speakers available</div>
                )}
              </div>
            </div>

            {/* Time Analysis Panel */}
            <div className="flex w-48 flex-col border-r border-slate-200 pr-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Time Analysis</h3>
              <div className="flex max-h-[calc(75vh-200px)] flex-col gap-2 overflow-y-auto">
                {speakersAnalysis?.time_analysis && speakersAnalysis.time_analysis.length > 0 ? (
                  speakersAnalysis.time_analysis.map((timeAnalysis, index) => {
                    const timeInSeconds = timeStringToSeconds(timeAnalysis.time);
                    const formattedTime = formatTime(timeInSeconds);
                    return (
                      <button
                        key={index}
                        onClick={() => handleTimeAnalysisSelect(index)}
                        className={`rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                          selectedTimeAnalysisIndex === index
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {formattedTime}
                      </button>
                    );
                  })
                ) : (
                  <div className="py-4 text-center text-sm text-slate-500">No time analysis available</div>
                )}
              </div>
            </div>

            {/* Cropping UI Panel */}
            <div className="flex flex-1 flex-col">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Cropping UI</h3>
              {isPersonContainerFeatureEnabled && (
                <div
                  className="mb-3 rounded-md border border-slate-200 bg-slate-50 p-3"
                  data-testid="person-container-preview"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-xs font-medium text-slate-700">Use person-container boundary</div>
                      <div className="text-xs text-slate-500">
                        {personContainerBoundingBoxMap[selectedSpeakerId]
                          ? 'Toggle to compare the generated upload crop.'
                          : 'No container was detected for this person.'}
                      </div>
                    </div>
                    <Toggle
                      size="small"
                      on={Boolean(personContainerBoundingBoxMap[selectedSpeakerId]) && usePersonContainerForPreview}
                      onToggle={setUsePersonContainerForPreview}
                      disabled={!personContainerBoundingBoxMap[selectedSpeakerId]}
                      title="Use person-container boundary in crop preview"
                    />
                  </div>
                  {calculatedCropPosition && (
                    <div className="mt-2 font-mono text-xs text-slate-600" data-testid="calculated-crop-position">
                      Crop: ({calculatedCropPosition.top_left.x.toFixed(4)},{' '}
                      {calculatedCropPosition.top_left.y.toFixed(4)}) → ({' '}
                      {calculatedCropPosition.bottom_right.x.toFixed(4)},{' '}
                      {calculatedCropPosition.bottom_right.y.toFixed(4)})
                    </div>
                  )}
                </div>
              )}
              <div className="relative flex flex-1 items-center justify-center overflow-hidden">
                <div ref={containerRef} className="relative overflow-hidden">
                  {videoLoading && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Icon name="IconLoader2" className="animate-spin" color="gray" size={20} stroke={2.5} />
                    </div>
                  )}
                  <video
                    src={videoUrl}
                    ref={videoRef}
                    onLoadedData={event => {
                      event.currentTarget.currentTime = currentCompositeFrame / fps;
                      setVideoLoading(false);
                    }}
                    className="size-full max-h-[calc(75vh-172px)] w-auto max-w-full object-contain"
                    style={{ opacity: videoLoading ? 0.5 : 1 }}
                    data-testid="crop-preview-image"
                  />

                  {/* Crop Selection Outline */}
                  <div className="absolute inset-0">
                    <CropSelectionOutline
                      facialBounds={facialBound ?? {}}
                      personBounds={personBound ?? {}}
                      personContainerBounds={isPersonContainerFeatureEnabled ? (personContainerBound ?? {}) : {}}
                      positions={cropPositions}
                      aspectRatio={containerAspectRatio}
                      videoAspectRatio={videoAspectRatio}
                      updatePositions={updateCropPositions}
                      maintainAspectRatio={maintainAspectRatio}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end border-t border-slate-200 pt-4">
            <div className="flex items-center space-x-3">
              <Button variation="outline" onClick={onClose} trackingId="video-crop-cancel-button">
                Cancel
              </Button>
              <Button variation="filled" onClick={handleCropComplete} trackingId="video-crop-save-button">
                Apply Crop
              </Button>
            </div>
          </div>
        </div>
      </ContentDialog>
    </div>
  );
}
